import { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { AsyncValidationFormContext } from './asyncValidationFormContext';
import {
  AsyncValidationFormContextState,
  AsyncValidationFormState,
  AsyncValidationStatus
} from './interfaces/async-validation-form';
import { useDontMountAtFirst } from '../../hooks/useDontMountAtFirst';
import { focusFirstErrorField } from '../../utils/dom';

interface AsyncValidationFormProps extends PropsWithChildren {
  focusFirstErrorFieldOnValidationError?: boolean;
}

export function AsyncValidationForm<AsyncFormState extends object = object>({
  children,
  focusFirstErrorFieldOnValidationError = true
}: AsyncValidationFormProps) {
  const [fields, setFields] = useState<AsyncValidationFormState<AsyncFormState>['fields']>(
    {} as AsyncValidationFormState<AsyncFormState>['fields']
  );
  const [isValidating, setIsValidating] = useState(false);
  const [runValidations, setRunValidations] = useState(false);

  const formikRef = useRef<FormikProps<unknown>>();
  const validationSuccessCallback = useRef<(formikValues: unknown) => void>();
  const validationErrorCallback = useRef<(formikErrors: unknown) => void>();

  useDontMountAtFirst(() => {
    if (runValidations && formikRef.current) {
      setIsValidating(true);

      formikRef.current!.validateForm().then(errors => {
        const errorKeys = Object.keys(errors) as Array<keyof AsyncFormState>;
        const isValidationSuccessful = errorKeys.length === 0;

        if (isValidationSuccessful && validationSuccessCallback.current) {
          validationSuccessCallback.current(formikRef.current!.values);
        }

        if (!isValidationSuccessful && validationErrorCallback.current) {
          validationErrorCallback.current(errors);
        }

        if (!isValidationSuccessful && focusFirstErrorFieldOnValidationError) {
          const touchedKeys = errorKeys.reduce((acc, curr) => {
            return {
              ...acc,
              [curr]: true
            };
          }, {});

          formikRef.current!.setTouched(touchedKeys, false).then(() => {
            setTimeout(() => {
              focusFirstErrorField(errorKeys as string[]);
            }, 0);
          });
        }

        formikRef.current = undefined;
        validationSuccessCallback.current = undefined;
        validationErrorCallback.current = undefined;

        setRunValidations(false);
        setIsValidating(false);
      });
    }
  }, [runValidations]);

  const registerField = useCallback(
    (fieldName: keyof AsyncFormState) => {
      setFields(prevState => ({
        ...prevState,
        [fieldName]: AsyncValidationStatus.INIT
      }));
    },
    [fields]
  );

  const setFieldStatus = useCallback(
    (fieldName: keyof AsyncFormState, status: AsyncValidationStatus) => {
      setFields(prevState => ({
        ...prevState,
        [fieldName]: status
      }));
    },
    [fields]
  );

  const runValidationsAndSubmit = useCallback(
    (
      formikProps: FormikProps<unknown>,
      successCallback: (formikValues: unknown) => void,
      errorCallback?: (formikErrors: unknown) => void
    ) => {
      if (formikProps.isValidating) {
        return;
      }

      setIsValidating(true);

      formikRef.current = formikProps;
      validationSuccessCallback.current = successCallback;
      validationErrorCallback.current = errorCallback;

      const newFields = { ...fields };

      Object.keys(fields).forEach(key => {
        newFields[key as keyof AsyncFormState] = AsyncValidationStatus.PENDING;
      });

      setFields(newFields);
      setRunValidations(true);
    },
    [fields]
  );

  const contextValues = useMemo<AsyncValidationFormContextState<AsyncFormState>>(() => {
    return {
      fields,
      setFieldStatus,
      registerAsyncField: registerField,
      runValidationsAndSubmit,
      isValidating
    };
  }, [setFieldStatus, fields, registerField, runValidationsAndSubmit, isValidating]);

  return (
    <AsyncValidationFormContext.Provider value={contextValues}>
      {children}
    </AsyncValidationFormContext.Provider>
  );
}
